/**
 * Campi del form di CREAZIONE cliente — componente presentazionale condiviso.
 *
 * Estratto da `CreaClientePage` per essere riusato dalla creazione inline in
 * `CambiaClienteModal` (R6-C) SENZA duplicare né ridurre il form: entrambi i
 * consumer usano lo stesso `clienteCreateSchema`, quindi valgono anche
 * `almeno_piva_o_cf` e `azienda_richiede_codice_o_pec`.
 *
 * Il componente non possiede stato: riceve `register` ed `errors` dal form
 * RHF del chiamante, che resta padrone di submit e gestione errori.
 */
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ClienteCreateData } from '../schemas/cliente';

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

function FieldWrap({
  label, error, required, children,
}: {
  label: string; error?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 py-2 border-b border-stone-mid last:border-0 items-start">
      <label className="text-xs text-ink-soft min-w-[160px] pt-2 flex-shrink-0">
        {label}
        {required && <span className="text-accent-danger ml-0.5">*</span>}
      </label>
      <div className="flex-1">
        {children}
        {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

interface Props {
  register: UseFormRegister<ClienteCreateData>;
  errors: FieldErrors<ClienteCreateData>;
}

export default function ClienteFormFields({ register, errors }: Props) {
  const { t } = useTranslation();
  return (
    <dl className="divide-y divide-stone-mid">
      <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} required error={errors.ragione_sociale?.message}>
        <input className={inputCls} data-testid="input-cliente-ragione-sociale" {...register('ragione_sociale')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
        <input className={inputCls} data-testid="input-cliente-piva-crea" {...register('piva')} maxLength={11} placeholder={t('anagrafiche.crea_cliente.ph_piva')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.codice_fiscale')} error={errors.codice_fiscale?.message}>
        <input className={inputCls} data-testid="input-cliente-cf" {...register('codice_fiscale')} maxLength={16} placeholder={t('anagrafiche.crea_cliente.ph_cf')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.indirizzo')} error={errors.indirizzo?.message}>
        <input className={inputCls} {...register('indirizzo')} placeholder={t('anagrafiche.crea_cliente.ph_indirizzo')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.cap')} error={errors.cap?.message}>
        <input className={inputCls} {...register('cap')} maxLength={5} placeholder={t('anagrafiche.crea_cliente.ph_cap')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.comune')} error={errors.citta?.message}>
        <input className={inputCls} {...register('citta')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.provincia')} error={errors.provincia?.message}>
        <input className={inputCls} {...register('provincia')} maxLength={2} placeholder={t('anagrafiche.crea_cliente.ph_provincia')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.nazione')} error={errors.nazione?.message}>
        <input className={inputCls} {...register('nazione')} maxLength={2} placeholder="IT" />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.codice_destinatario')} error={errors.codice_destinatario?.message}>
        <input className={inputCls} data-testid="input-cliente-codice-dest" {...register('codice_destinatario')} maxLength={7} placeholder={t('anagrafiche.crea_cliente.ph_codice_dest')} />
        {/* FIX 3: l'helper sparisce quando c'e' l'errore rosso, altrimenti lo
            stesso testo comparirebbe due volte sotto lo stesso campo. */}
        {!errors.codice_destinatario && (
          <p className="text-xs text-ink-faint mt-0.5">{t('anagrafiche.crea_cliente.hint_azienda_codice_o_pec')}</p>
        )}
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.referente')} error={errors.referente?.message}>
        <input className={inputCls} {...register('referente')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.telefono')} error={errors.telefono?.message}>
        <input className={inputCls} {...register('telefono')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.email')} required error={errors.email?.message}>
        <input className={inputCls} data-testid="input-cliente-email" type="email" {...register('email')} />
      </FieldWrap>
      <FieldWrap label={t('anagrafiche.campi.pec')} error={errors.pec?.message}>
        <input className={inputCls} data-testid="input-cliente-pec" type="email" {...register('pec')} />
        {!errors.pec && !errors.codice_destinatario && (
          <p className="text-xs text-ink-faint mt-0.5">{t('anagrafiche.crea_cliente.hint_azienda_codice_o_pec')}</p>
        )}
      </FieldWrap>
    </dl>
  );
}
